<template>
	<div>
		<div class="font-semibold mb-3 text-ihh-midnight-gray text-sm">
			<span>{{ modifier.displayName }}:</span>
			<span class="text-ihh-slate-gray font-normal ml-1">Required</span>
		</div>

		<div class="flex flex-col gap-2">
			<div
				v-if="!uploadedFile"
				class="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-ihh-dark-salmon transition-colors cursor-pointer"
				@click="triggerFileInput"
				@dragover.prevent="isDragging = true"
				@dragleave.prevent="isDragging = false"
				@drop.prevent="handleDrop"
				:class="{ 'border-ihh-dark-salmon bg-ihh-pink/10': isDragging }"
			>
				<input
					ref="fileInput"
					type="file"
					accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
					class="hidden"
					@change="handleFileSelect"
				/>
				<div class="flex flex-col items-center justify-center text-center">
					<svg class="w-10 h-10 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
						/>
					</svg>
					<p class="text-sm text-ihh-slate-gray mb-1"><span class="text-ihh-dark-salmon font-medium">Click to upload</span> or drag and drop</p>
					<p class="text-xs text-gray-400">{{ supportedTypesText }}</p>
					<p v-if="maxSizeText" class="text-xs text-gray-400">{{ maxSizeText }}</p>
				</div>
			</div>

			<div v-if="uploadedFile && !uploading" class="flex items-center gap-3 p-4 bg-ihh-pink/20 border border-ihh-dark-salmon/30 rounded-lg">
				<div class="shrink-0">
					<img v-if="isImage" :src="uploadedFile.signedUrl || uploadedFile.url" class="w-14 h-14 object-cover rounded-md" />
					<div v-else class="w-14 h-14 bg-gray-200 rounded-md flex items-center justify-center">
						<svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
					</div>
				</div>
				<div class="flex-1 min-w-0">
					<p class="text-sm font-medium text-ihh-midnight-gray truncate">{{ uploadedFile.filename }}</p>
					<p class="text-xs text-ihh-slate-gray">Uploaded successfully</p>
				</div>
				<button @click="removeFile" class="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>

			<p v-if="error" class="text-sm text-red-500">{{ error }}</p>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import { storedAuthorization } from '../../../helpers/session.js'

export default {
	inject: ['store'],
	name: 'FileModifier',
	props: {
		modifier: {
			type: Object,
			required: true,
		},
		product: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			isDragging: false,
			uploading: false,
			uploadedFile: null,
			error: null,
		}
	},
	computed: {
		acceptedTypes() {
			const config = this.modifier.config || {}
			if (config.fileTypesMode === 'specific' && config.fileTypesSupported?.length) {
				const types = []
				if (config.fileTypesSupported?.includes('images')) {
					types.push('image/png', 'image/jpeg', 'image/gif', 'image/webp', '.png', '.jpg', '.jpeg', '.gif', '.webp')
				}
				if (config.fileTypesSupported?.includes('documents')) {
					types.push(
						'application/pdf',
						'application/msword',
						'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
						'application/vnd.ms-excel',
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
						'application/vnd.ms-powerpoint',
						'application/vnd.openxmlformats-officedocument.presentationml.presentation',
						'text/plain',
						'application/rtf',
						'.pdf',
						'.doc',
						'.docx',
						'.xls',
						'.xlsx',
						'.ppt',
						'.pptx',
						'.txt',
						'.rtf',
					)
				}
				if (config.fileTypesOther?.length) {
					types.push(...config.fileTypesOther)
				}
				return types.length ? types.join(',') : ''
			}
			// Default: accept all common file types including images and documents
			return 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf'
		},
		supportedTypesText() {
			const config = this.modifier.config || {}
			if (config.fileTypesMode === 'specific') {
				const types = []
				if (config.fileTypesSupported?.includes('images')) {
					types.push('Images (PNG, JPG, GIF, WebP)')
				}
				if (config.fileTypesSupported?.includes('documents')) {
					types.push('Documents (PDF, Word, Excel, PowerPoint, TXT)')
				}
				if (config.fileTypesOther?.length) {
					types.push(...config.fileTypesOther)
				}
				return types.length ? types.join(', ') : 'All file types accepted'
			}
			return 'All file types accepted'
		},
		maxSizeText() {
			const maxSizeKB = this.modifier.config?.fileMaxSize
			if (maxSizeKB) {
				const maxSizeBytes = maxSizeKB * 1024
				const mb = maxSizeBytes / (1024 * 1024)
				if (mb >= 1) {
					return `Max size: ${mb.toFixed(1)} MB`
				}
				return `Max size: ${maxSizeKB.toFixed(0)} KB`
			}
			return null
		},
		isImage() {
			if (!this.uploadedFile?.url) return false
			return /\.(jpg|jpeg|png|gif|webp)$/i.test(this.uploadedFile.url)
		},
	},
	methods: {
		// Logged-in members authenticate with their token; guests get a short-lived upload ticket.
		async uploadHeadersAndTicket() {
			const authorization = storedAuthorization()
			if (authorization) return { headers: { Authorization: authorization }, ticket: undefined }
			const { data } = await axios.post(`${this.store.api}/controllers/files/ticket`)
			const ticket = data?.ticket || data?.response?.ticket
			if (!ticket) throw new Error('No upload ticket returned')
			return { headers: {}, ticket }
		},
		uploadErrorMessage(err) {
			const body = err?.response?.data
			const detail = typeof body?.error === 'string' ? body.error : typeof body?.response === 'string' ? body.response : typeof body?.message === 'string' ? body.message : ''
			const status = err?.response?.status
			if (status === 413) return 'That file is too large to upload.'
			if (status === 429) return 'Too many upload attempts. Please wait a moment and try again.'
			if (status >= 400 && status < 500 && detail) return detail
			return null
		},
		triggerFileInput() {
			this.$refs.fileInput.click()
		},
		handleDrop(e) {
			this.isDragging = false
			const files = e.dataTransfer.files
			if (files.length > 0) {
				this.uploadFile(files[0])
			}
		},
		handleFileSelect(e) {
			const files = e.target.files
			if (files.length > 0) {
				this.uploadFile(files[0])
			}
		},
		async uploadFile(file) {
			this.error = null

			const maxSizeKB = this.modifier.config?.fileMaxSize
			const maxSizeBytes = maxSizeKB ? maxSizeKB * 1024 : null
			if (maxSizeBytes && file.size > maxSizeBytes) {
				const mb = maxSizeBytes / (1024 * 1024)
				this.error = `File size exceeds maximum allowed (${mb >= 1 ? mb.toFixed(1) + ' MB' : maxSizeKB.toFixed(0) + ' KB'})`
				return
			}

			this.uploading = true
			this.$emit('uploadStart')

			try {
				const base64Data = await this.fileToBase64(file)

				const { headers, ticket } = await this.uploadHeadersAndTicket()
				const { data } = await axios.post(`${this.store.api}/controllers/files/upload`, {
					title: file.name,
					filename: file.name,
					mimeType: file.type,
					size: file.size,
					modifierId: this.modifier.id,
					modifierName: this.modifier.displayName,
					productId: this.product?.id || null,
					productName: this.product?.name || null,
					fileData: base64Data,
					ticket,
				}, { headers })

				const fileRecord = data.response

				if (!fileRecord?.url) {
					throw new Error('File upload failed — no URL returned from server')
				}

				// const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
				const adminBaseUrl = 'https://admin.inhishands.com'
				const adminUrl = `${adminBaseUrl}/files/${fileRecord.id}`

				this.uploadedFile = {
					url: fileRecord.url,
					signedUrl: fileRecord.signedUrl,
					filename: fileRecord.filename,
					fileId: fileRecord.id,
					adminUrl,
				}

				this.$emit('fileUploaded', {
					modifierId: this.modifier.id,
					modifierName: this.modifier.displayName,
					url: fileRecord.url,
					filename: fileRecord.filename,
					fileId: fileRecord.id,
					adminUrl,
				})
			} catch (err) {
				console.error('Upload error:', err)
				this.error = this.uploadErrorMessage(err) || 'Failed to upload file. Please try again.'
			} finally {
				this.uploading = false
				this.$emit('uploadEnd')
			}
		},
		fileToBase64(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.readAsDataURL(file)
				reader.onload = () => resolve(reader.result)
				reader.onerror = (error) => reject(error)
			})
		},
		removeFile() {
			this.uploadedFile = null
			this.$emit('fileUploaded', {
				modifierId: this.modifier.id,
				modifierName: this.modifier.displayName,
				url: null,
				filename: null,
				fileId: null,
			})
		},
		getUploadedFile() {
			return this.uploadedFile
		},
		clearFile() {
			this.uploadedFile = null
		},
	},
}
</script>

<style scoped></style>
